import {
  entityRegistry,
  type EntityDescriptor,
  type FieldDescriptor,
} from '@rammetec/data-access'

/**
 * Mirror of `@syscolect/domain` `SERVICE_FILLED_NAMES` (request 0320).
 * Engine boundary (spec 0051 D1) forbids importing the domain package — keep
 * this list in sync; `serviceFilledFields.parity.test.ts` compares both.
 */
export const SERVICE_FILLED_NAMES: ReadonlySet<string> = new Set([
  'id',
  'organizationId',
  'version',
  'active',
  'inclusionDate',
  'lastModifiedDate',
  'inclusionUserId',
  'lastModifiedUserId',
  'inactivationDate',
  // ProductGroup hierarchy (request 0347) — stamped by productGroupService
  'path',
  'level',
  'pathDescription',
])

const SCALAR_TYPES = new Set<FieldDescriptor['type']>([
  'string',
  'number',
  'boolean',
  'date',
  'enum',
])

export interface RelationMatchKey {
  key: 'code' | 'description'
  field: string
  label: string
}

export interface ImportableField {
  name: string
  label: string
  descriptor: FieldDescriptor
  /** `descriptor.required && !autoNumber` */
  required: boolean
  autoNumber: boolean
  unique: boolean
  relation?: { targetEntity: string; matchKeys: RelationMatchKey[] }
}

function groupsIntersect(
  fieldGroups: readonly string[] | undefined,
  activeGroups: readonly string[],
): boolean {
  if (!fieldGroups || fieldGroups.length === 0) return true
  return fieldGroups.some((g) => activeGroups.includes(g))
}

function buildRelationMatchKeys(
  targetEntity: string,
  displayField: string,
): RelationMatchKey[] {
  const keys: RelationMatchKey[] = []
  if (entityRegistry.has(targetEntity)) {
    const target = entityRegistry.get(targetEntity)
    const uniqueField = target.fields.find((f) => f.unique === true)
    if (uniqueField) {
      keys.push({ key: 'code', field: uniqueField.name, label: 'Código' })
    }
    const descField =
      target.fields.find((f) => f.name === displayField) ??
      ({ name: displayField, label: displayField } as FieldDescriptor)
    keys.push({
      key: 'description',
      field: displayField,
      label: descField.label || displayField,
    })
  } else {
    keys.push({ key: 'description', field: displayField, label: displayField })
  }
  return keys
}

/**
 * Fields the import dialog (and the assistant, after dropping autoNumber) may fill.
 * Order follows the descriptor. Duplicate labels: first wins.
 */
export function resolveImportableFields(
  descriptor: EntityDescriptor,
  opts?: { activeGroups?: readonly string[]; exclude?: readonly string[] },
): ImportableField[] {
  const exclude = new Set(opts?.exclude ?? [])
  const activeGroups = opts?.activeGroups
  const fkByName = new Map((descriptor.foreignKeys ?? []).map((fk) => [fk.fieldName, fk]))
  const seenLabels = new Set<string>()
  const result: ImportableField[] = []

  for (const field of descriptor.fields) {
    if (SERVICE_FILLED_NAMES.has(field.name) || field.virtual) continue
    if (exclude.has(field.name)) continue
    if (field.type === 'array' || field.type === 'object') continue
    if (activeGroups !== undefined && !groupsIntersect(field.groups, activeGroups)) {
      continue
    }

    const autoNumber = field.autoNumber === true
    const fk = fkByName.get(field.name)

    if (fk) {
      if (seenLabels.has(field.label)) continue
      seenLabels.add(field.label)
      const displayField = fk.displayField ?? 'description'
      result.push({
        name: field.name,
        label: field.label,
        descriptor: field,
        required: field.required === true && !autoNumber,
        autoNumber,
        unique: field.unique === true,
        relation: {
          targetEntity: fk.targetEntity,
          matchKeys: buildRelationMatchKeys(fk.targetEntity, displayField),
        },
      })
      continue
    }

    if (!SCALAR_TYPES.has(field.type) && !autoNumber) continue
    if (seenLabels.has(field.label)) continue
    seenLabels.add(field.label)

    result.push({
      name: field.name,
      label: field.label,
      descriptor: field,
      required: field.required === true && !autoNumber,
      autoNumber,
      unique: field.unique === true,
    })
  }

  return result
}
